import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  InputProps,
} from '@chakra-ui/react';
import {
  FieldPathByValue,
  FieldValues,
  get,
  useFormContext,
} from 'react-hook-form';
import { Icon } from 'src/components/atoms/Icon';
import { AvailableIcon } from 'src/components/atoms/Icon';

type BaseFormTextInputProps = {
  required?: boolean;
  placeholder: string;
  label?: string;
  helperText?: string;
  type?: string;
  sx?: FormControlProps;
  inputProps?: InputProps;
  id?: string;
  leftIcon?: {
    iconName: AvailableIcon;
    width?: number;
    height?: number;
    color?: string;
  };
  rightIcon?: {
    iconName: AvailableIcon;
    width?: number;
    height?: number;
    color?: string;
  };
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  readOnly?: boolean;
  error?: { message?: string };
};

type FormTextInputWithFormProps<FormInputs extends FieldValues> =
  BaseFormTextInputProps & {
    inputPath: FieldPathByValue<FormInputs, string>;
    useForm?: true;
  };

type FormTextInputWithoutFormProps = BaseFormTextInputProps & {
  inputPath?: never;
  useForm?: false;
};

type FormTextInputProps<FormInputs extends FieldValues> =
  | FormTextInputWithFormProps<FormInputs>
  | FormTextInputWithoutFormProps;

// Internal component that uses form context
function FormTextInputWithForm<FormInputs extends FieldValues>({
  inputPath,
  required,
  label,
  placeholder,
  helperText,
  type,
  sx,
  inputProps,
  id,
  leftIcon,
  rightIcon,
}: FormTextInputWithFormProps<FormInputs>) {
  const formContext = useFormContext<FormInputs>();
  const error = inputPath
    ? get(formContext.formState.errors, inputPath)
    : undefined;

  const inputId = id || inputPath;

  const renderInput = () => {
    const baseInputProps: InputProps = {
      id: inputId,
      placeholder,
      type,
      ...inputProps,
    };

    const input = (
      <Input
        {...formContext.register(inputPath, {
          required: required ? 'Required' : false,
        })}
        {...baseInputProps}
      />
    );

    if (leftIcon || rightIcon) {
      return (
        <InputGroup>
          {leftIcon && (
            <InputLeftElement>
              <Icon
                iconName={leftIcon.iconName}
                width={leftIcon.width || 5}
                height={leftIcon.height || 5}
                color={leftIcon.color || 'gray.400'}
              />
            </InputLeftElement>
          )}
          {input}
          {rightIcon && (
            <InputRightElement pointerEvents="none">
              <Icon
                iconName={rightIcon.iconName}
                width={rightIcon.width || 5}
                height={rightIcon.height || 5}
                color={rightIcon.color || 'gray.400'}
              />
            </InputRightElement>
          )}
        </InputGroup>
      );
    }

    return input;
  };

  return (
    <FormControl isInvalid={!!error} {...sx}>
      {label && (
        <FormLabel
          htmlFor={inputId}
          fontFamily="marketingPage"
          fontWeight="medium"
          color="text.title"
          mb={2}>
          {label}
        </FormLabel>
      )}
      {renderInput()}
      {error?.message && typeof error.message === 'string' && (
        <FormErrorMessage>{error.message}</FormErrorMessage>
      )}
      {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

// Internal component that doesn't use form context
function FormTextInputWithoutForm({
  label,
  placeholder,
  helperText,
  type,
  sx,
  inputProps,
  id,
  leftIcon,
  rightIcon,
  value,
  onChange,
  onClick,
  readOnly,
  error,
}: FormTextInputWithoutFormProps) {
  const renderInput = () => {
    const baseInputProps: InputProps = {
      id,
      placeholder,
      type,
      ...inputProps,
      ...(value !== undefined && { value }),
      ...(onChange && { onChange }),
      ...(onClick && { onClick }),
      ...(readOnly !== undefined && { readOnly }),
    };

    const input = <Input {...baseInputProps} />;

    if (leftIcon || rightIcon) {
      return (
        <InputGroup>
          {leftIcon && (
            <InputLeftElement>
              <Icon
                iconName={leftIcon.iconName}
                width={leftIcon.width || 5}
                height={leftIcon.height || 5}
                color={leftIcon.color || 'gray.400'}
              />
            </InputLeftElement>
          )}
          {input}
          {rightIcon && (
            <InputRightElement pointerEvents="none">
              <Icon
                iconName={rightIcon.iconName}
                width={rightIcon.width || 5}
                height={rightIcon.height || 5}
                color={rightIcon.color || 'gray.400'}
              />
            </InputRightElement>
          )}
        </InputGroup>
      );
    }

    return input;
  };

  return (
    <FormControl isInvalid={!!error} {...sx}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontFamily="marketingPage"
          fontWeight="medium"
          color="text.title"
          mb={2}>
          {label}
        </FormLabel>
      )}
      {renderInput()}
      {error?.message && typeof error.message === 'string' && (
        <FormErrorMessage>{error.message}</FormErrorMessage>
      )}
      {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export function FormTextInput<FormInputs extends FieldValues>(
  props: FormTextInputProps<FormInputs>,
) {
  if (props.useForm === false || !props.inputPath) {
    return (
      <FormTextInputWithoutForm {...(props as FormTextInputWithoutFormProps)} />
    );
  }
  return (
    <FormTextInputWithForm
      {...(props as FormTextInputWithFormProps<FormInputs>)}
    />
  );
}
